import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "./Admin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, Check, CreditCard, Mail, Undo2, Calendar as CalIcon, Package, FileDown, Send, History, MinusCircle, Receipt, ExternalLink, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { EditBookingDialog } from "@/components/admin/EditBookingDialog";

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtDate = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString() : "—";

const exportReconCsv = (recons: any[], bookingId: string) => {
  const headers = [
    "created_at","event_type","stripe_event_id","source",
    "before_total","after_total",
    "before_amount_paid","after_amount_paid","amount_paid_delta",
    "before_payment_status","after_payment_status",
    "before_status","after_status",
    "refunded_amount","refund_id","refund_status","failure_reason",
    "full_refund","invoice_email_status","invoice_email_template","synthetic",
  ];
  const esc = (v: any) => {
    const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = recons.map((r) => {
    const b = r.before_snapshot ?? {}; const a = r.after_snapshot ?? {}; const d = r.deltas ?? {};
    return [
      r.created_at, r.event_type, r.stripe_event_id, r.source,
      b.total, a.total,
      b.amount_paid, a.amount_paid, d.amount_paid_delta,
      b.payment_status, a.payment_status,
      b.status, a.status,
      d.refunded_amount, d.refund_id, d.refund_status, d.failure_reason,
      d.full_refund, d.invoice_email?.last_status, d.invoice_email?.last_template, d.synthetic,
    ].map(esc).join(",");
  });
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `reconciliations-${bookingId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link); link.click(); link.remove();
  URL.revokeObjectURL(link.href);
};

type Booking = any;
type Addon = any;
type EmailLog = any;
type WebhookEvent = any;
type SyncLog = any;
type AuditEntry = any;

const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, string> = {
    pending: "bg-amber-500/15 text-amber-500 border-amber-500/40",
    confirmed: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
    completed: "bg-blue-500/15 text-blue-500 border-blue-500/40",
    cancelled: "bg-muted text-muted-foreground border-border",
    refunded: "bg-destructive/15 text-destructive border-destructive/40",
    paid: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
    unpaid: "bg-muted text-muted-foreground border-border",
    partial: "bg-amber-500/15 text-amber-500 border-amber-500/40",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs uppercase tracking-widest font-mono border ${map[status] ?? "bg-muted border-border"}`}>
      {status}
    </span>
  );
};

const TimelineItem = ({
  done, label, at, icon: Icon, detail,
}: { done: boolean; label: string; at?: string | null; icon: any; detail?: string }) => (
  <div className="flex gap-4 items-start">
    <div className={`mt-1 w-8 h-8 flex items-center justify-center border ${done ? "bg-primary text-primary-foreground border-primary" : "bg-panel text-muted-foreground border-border"}`}>
      {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
    </div>
    <div className="flex-1 pb-6 border-l border-border pl-4 -ml-4 ml-8">
      <p className="font-body text-sm">{label}</p>
      <p className="text-xs font-mono text-muted-foreground">{at ? fmtDate(at) : "pending"}</p>
      {detail && <p className="text-xs text-muted-foreground mt-1">{detail}</p>}
    </div>
  </div>
);

const AdminBookingDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [studio, setStudio] = useState<any>(null);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [emails, setEmails] = useState<EmailLog[]>([]);
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [resending, setResending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [actors, setActors] = useState<Record<string, string>>({});
  const [resendingInvoice, setResendingInvoice] = useState(false);
  const [sendingStripeInvoice, setSendingStripeInvoice] = useState(false);
  const [recons, setRecons] = useState<any[]>([]);

  // Partial refund dialog state
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAddonIds, setRefundAddonIds] = useState<string[]>([]);
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [refundReason, setRefundReason] = useState<string>("");
  const [refundReasonCode, setRefundReasonCode] = useState<string>("");
  const [refundInternalNotes, setRefundInternalNotes] = useState<string>("");
  const [refundNotify, setRefundNotify] = useState(true);
  const [refunding, setRefunding] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    const { data: b } = await supabase.from("bookings").select("*").eq("id", id).maybeSingle();
    setBooking(b);
    if (b?.studio_id) {
      const { data: s } = await supabase.from("studios").select("*").eq("id", b.studio_id).maybeSingle();
      setStudio(s);
    }
    const [{ data: a }, { data: el }, { data: ev }, { data: sl }] = await Promise.all([
      supabase.from("booking_addons").select("*, addons(name)").eq("booking_id", id),
      supabase.from("email_send_log").select("*").contains("metadata", { booking_id: id }).order("created_at", { ascending: false }),
      supabase.from("stripe_webhook_events").select("*").eq("related_id", id).order("received_at", { ascending: false }),
      supabase.from("calendar_sync_log").select("*").eq("booking_id", id).order("created_at", { ascending: false }),
    ]);
    setAddons(a ?? []);
    setEmails(el ?? []);
    setEvents(ev ?? []);
    setSyncLogs(sl ?? []);

    const { data: au } = await supabase
      .from("admin_audit_log")
      .select("*")
      .eq("entity_type", "booking")
      .eq("entity_id", id)
      .order("created_at", { ascending: false });
    setAudit(au ?? []);
    const { data: rc } = await (supabase as any)
      .from("booking_reconciliations")
      .select("*")
      .eq("booking_id", id)
      .order("created_at", { ascending: false });
    setRecons(rc ?? []);
    const actorIds = Array.from(new Set((au ?? []).map((r: any) => r.actor_id).filter(Boolean)));
    if (actorIds.length) {
      const { data: profs } = await supabase
        .from("profiles").select("id,display_name").in("id", actorIds as string[]);
      const m: Record<string, string> = {};
      (profs ?? []).forEach((p: any) => { m[p.id] = p.display_name ?? p.id.slice(0, 8); });
      setActors(m);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const resend = async () => {
    if (!booking) return;
    setResending(true);
    try {
      const { error } = await supabase.functions.invoke("resend-booking-confirmation", { body: { booking_id: booking.id } });
      if (error) throw error;
      toast.success("Confirmation re-sent");
      load();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to resend");
    } finally {
      setResending(false);
    }
  };

  const cancel = async () => {
    if (!booking) return;
    const reason = prompt(
      "Cancel this booking? No refund will be issued automatically — use 'Partial refund' for that.\n\nOptional reason for the customer:",
      "",
    );
    if (reason === null) return;
    const { data, error } = await supabase.functions.invoke("cancel-booking", {
      body: { booking_id: booking.id, reason, notify_customer: true },
    });
    if (error || (data as any)?.error) return toast.error(error?.message || (data as any)?.error);
    toast.success("Booking cancelled — no refund issued");
    load();
  };

  const deleteBooking = async () => {
    if (!booking) return;
    const paid = Number(booking.amount_paid) > 0;
    const warning = paid
      ? `This booking has $${Number(booking.amount_paid).toFixed(2)} paid. Deleting REMOVES the record entirely — it will NOT issue a refund. Issue any refund first via "Partial refund".\n\nType DELETE to confirm permanent removal:`
      : `Permanently delete this booking? This cannot be undone.\n\nType DELETE to confirm:`;
    const confirm = prompt(warning, "");
    if (confirm !== "DELETE") return;
    try {
      // Best-effort: remove from Google Calendar before deleting the row.
      try {
        await supabase.functions.invoke("cancel-booking", {
          body: { booking_id: booking.id, reason: "deleted by admin", notify_customer: false },
        });
      } catch {
        // ignore — booking may already be cancelled
      }
      const { error } = await supabase.from("bookings").delete().eq("id", booking.id);
      if (error) throw error;
      await supabase.from("admin_audit_log").insert({
        action: "delete_booking",
        entity_type: "booking",
        entity_id: booking.id,
        after: {
          total: booking.total,
          amount_paid: booking.amount_paid,
          status: booking.status,
          payment_status: booking.payment_status,
        },
      });
      toast.success("Booking deleted");
      navigate("/admin/bookings");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to delete booking");
    }
  };

  const resendInvoiceEmail = async () => {
    if (!booking) return;
    setResendingInvoice(true);
    try {
      const { data, error } = await supabase.functions.invoke("resend-booking-invoice", {
        body: { booking_id: booking.id },
      });
      if (error || (data as any)?.error) throw new Error(error?.message || (data as any)?.error);
      toast.success(`Invoice link emailed to ${(data as any)?.recipient}`);
      load();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to email invoice");
    } finally {
      setResendingInvoice(false);
    }
  };

  const sendStripeInvoice = async () => {
    if (!booking) return;
    const ok = window.confirm(
      "Send a Stripe-hosted invoice to the customer? They'll receive an email from Stripe with a Pay button (card / ACH). A 2.9% + $0.30 processing fee is added.",
    );
    if (!ok) return;
    setSendingStripeInvoice(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-send-invoice", {
        body: { booking_id: booking.id },
      });
      if (error || (data as any)?.error) throw new Error(error?.message || (data as any)?.error);
      const url = (data as any)?.hosted_invoice_url;
      toast.success(`Stripe invoice sent to ${(data as any)?.recipient}`);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      load();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to send Stripe invoice");
    } finally {
      setSendingStripeInvoice(false);
    }
  };

  const openRefund = () => {
    setRefundAddonIds([]);
    setRefundAmount("");
    setRefundReason("");
    setRefundReasonCode("");
    setRefundInternalNotes("");
    setRefundNotify(true);
    setRefundOpen(true);
  };

  const selectedAddonsTotal = addons
    .filter((a) => refundAddonIds.includes(a.id))
    .reduce((s, a) => s + Number(a.unit_price) * Number(a.quantity || 1), 0);

  const submitPartialRefund = async () => {
    if (!booking) return;
    const amt = refundAmount ? Number(refundAmount) : 0;
    if (amt <= 0 && refundAddonIds.length === 0) {
      return toast.error("Pick add-ons to remove or enter a refund amount");
    }
    if (!refundReasonCode) return toast.error("Pick a refund reason category");
    if (refundReason.trim().length < 5)
      return toast.error("Enter a refund reason (min 5 chars) — shared with the customer");
    setRefunding(true);
    try {
      const { data, error } = await supabase.functions.invoke("booking-partial-refund", {
        body: {
          booking_id: booking.id,
          remove_addon_ids: refundAddonIds,
          refund_amount: amt > 0 ? amt : undefined,
          reason: refundReason,
          reason_code: refundReasonCode,
          internal_notes: refundInternalNotes,
          notify_customer: refundNotify,
        },
      });
      if (error || (data as any)?.error) throw new Error(error?.message || (data as any)?.error);
      toast.success(`Refunded ${fmtMoney(Number((data as any)?.refund_amount ?? 0))}`);
      setRefundOpen(false);
      load();
    } catch (e: any) {
      toast.error(e?.message ?? "Refund failed");
    } finally {
      setRefunding(false);
    }
  };

  const downloadInvoice = async () => {
    if (!booking) return;
    setDownloading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/booking-invoice?booking_id=${booking.id}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${session?.access_token ?? ""}` },
      });
      if (!res.ok) throw new Error(`Invoice request failed (${res.status})`);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `invoice-${booking.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to download invoice");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="BOOKING">
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      </AdminLayout>
    );
  }
  if (!booking) {
    return (
      <AdminLayout title="BOOKING">
        <p className="font-body text-muted-foreground">Booking not found.</p>
        <Link to="/admin/bookings" className="text-primary font-body text-sm mt-4 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to bookings
        </Link>
      </AdminLayout>
    );
  }

  const addonsTotal = addons.reduce((s, r) => s + Number(r.unit_price) * Number(r.quantity || 1), 0);
  const refundEvents = events.filter((e) => /refund/i.test(e.type));

  // ---- Invoice email send tracking ----
  const invoiceEmails = emails
    .filter((e) => e.template_name === "booking-invoice-link" || e.template_name === "booking-confirmation")
    .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
  const latestInvoiceEmail = invoiceEmails[0] ?? null;
  const latestInvoiceWebhookEvent = events.find((e) =>
    /invoice|email/i.test(e.type) || e.related_type === "booking",
  );

  // Derive timeline timestamps
  const createdAt = booking.created_at;
  const paidEvent = events.find((e) => e.type === "checkout.session.completed" || e.type === "payment_intent.succeeded");
  const paidAt = paidEvent?.processed_at ?? (booking.payment_status === "paid" ? booking.updated_at : null);
  const confirmedAt = booking.status === "confirmed" || booking.status === "completed" ? (paidAt ?? booking.updated_at) : null;
  const completedAt = booking.status === "completed" ? booking.updated_at : null;
  const cancelledAt = booking.status === "cancelled" || booking.status === "refunded" ? booking.updated_at : null;

  return (
    <AdminLayout title="BOOKING">
      <Link to="/admin/bookings" className="text-primary font-body text-sm inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to bookings
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display tracking-widest text-2xl">{studio?.name ?? "Studio"}</h2>
          <p className="font-body text-muted-foreground">
            {fmtDate(booking.start_at)} → {new Date(booking.end_at).toLocaleTimeString()}
          </p>
          <p className="font-mono text-xs text-muted-foreground mt-1">ID: {booking.id}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <StatusBadge status={booking.status} />
            <StatusBadge status={booking.payment_status} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} className="font-body">
            <Pencil className="w-4 h-4 mr-1" /> Edit
          </Button>
          <Button variant="outline" onClick={downloadInvoice} disabled={downloading} className="font-body">
            {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
            Invoice PDF
          </Button>
          <Button variant="outline" onClick={resendInvoiceEmail} disabled={resendingInvoice} className="font-body">
            {resendingInvoice ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Email invoice
          </Button>
          <Button onClick={sendStripeInvoice} disabled={sendingStripeInvoice} className="font-body bg-primary text-primary-foreground">
            {sendingStripeInvoice ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Receipt className="w-4 h-4 mr-1" />}
            Send Stripe invoice
          </Button>
          {booking.stripe_invoice_url && (
            <a
              href={booking.stripe_invoice_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center font-body text-xs underline text-muted-foreground self-center"
            >
              View hosted invoice <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          )}
          <Button variant="outline" onClick={resend} disabled={resending} className="font-body">
            {resending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
            Resend confirmation
          </Button>
          {Number(booking.amount_paid) > 0 && booking.status !== "cancelled" && booking.payment_status !== "refunded" && (
            <Button variant="outline" onClick={openRefund} className="font-body text-destructive">
              <MinusCircle className="w-4 h-4 mr-1" /> Partial refund
            </Button>
          )}
          {booking.status !== "cancelled" && booking.status !== "refunded" && (
            <Button variant="outline" onClick={cancel} className="font-body text-destructive">
              <Undo2 className="w-4 h-4 mr-1" /> Cancel booking
            </Button>
          )}
          <Button variant="outline" onClick={deleteBooking} className="font-body text-destructive">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="bg-panel border border-border p-6 lg:col-span-1">
          <h3 className="font-display tracking-widest text-sm mb-6">TIMELINE</h3>
          <TimelineItem done={!!createdAt} label="Created" at={createdAt} icon={CalIcon} />
          <TimelineItem done={!!paidAt} label="Payment received" at={paidAt} icon={CreditCard}
            detail={paidAt ? `${fmtMoney(Number(booking.amount_paid))} via Stripe` : undefined} />
          <TimelineItem done={!!confirmedAt} label="Confirmed" at={confirmedAt} icon={Check} />
          <TimelineItem done={!!completedAt} label="Completed" at={completedAt} icon={Check} />
          {cancelledAt && (
            <TimelineItem done label={booking.status === "refunded" ? "Refunded" : "Cancelled"} at={cancelledAt} icon={Undo2} />
          )}
        </div>

        {/* Summary + addons */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-panel border border-border p-6">
            <h3 className="font-display tracking-widest text-sm mb-4">SUMMARY</h3>
            <div className="grid grid-cols-2 gap-3 font-body text-sm">
              <div className="text-muted-foreground">Subtotal</div><div className="text-right">{fmtMoney(Number(booking.subtotal))}</div>
              <div className="text-muted-foreground">Add-ons</div><div className="text-right">{fmtMoney(addonsTotal)}</div>
              <div className="text-muted-foreground">Total</div><div className="text-right font-display text-primary">{fmtMoney(Number(booking.total))}</div>
              <div className="text-muted-foreground">Amount paid</div><div className="text-right">{fmtMoney(Number(booking.amount_paid))}</div>
              {booking.stripe_payment_intent && (
                <>
                  <div className="text-muted-foreground">Payment Intent</div>
                  <div className="text-right font-mono text-xs break-all">{booking.stripe_payment_intent}</div>
                </>
              )}
            </div>
            {booking.notes && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-1">Notes</p>
                <p className="font-body text-sm whitespace-pre-wrap">{booking.notes}</p>
              </div>
            )}
          </div>

          <div className="bg-panel border border-border p-6">
            <h3 className="font-display tracking-widest text-sm mb-4 flex items-center gap-2">
              <Package className="w-4 h-4" /> ADD-ONS ({addons.length})
            </h3>
            {addons.length === 0 ? (
              <p className="font-body text-sm text-muted-foreground">No add-ons selected.</p>
            ) : (
              <div className="space-y-2">
                {addons.map((a) => (
                  <div key={a.id} className="flex justify-between items-center text-sm font-body border-b border-border pb-2 last:border-0">
                    <div>
                      <span className="font-body">{a.addons?.name ?? "Add-on"}</span>
                      {a.option_label && <span className="text-muted-foreground"> — {a.option_label}</span>}
                    </div>
                    <div className="text-right">
                      <span className="text-muted-foreground">{a.quantity} × {fmtMoney(Number(a.unit_price))}</span>
                      <span className="ml-3 font-display text-primary">{fmtMoney(Number(a.unit_price) * a.quantity)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {refundEvents.length > 0 && (
            <div className="bg-panel border border-border p-6">
              <h3 className="font-display tracking-widest text-sm mb-4">REFUNDS</h3>
              <div className="space-y-2">
                {refundEvents.map((e) => (
                  <div key={e.id} className="flex justify-between text-sm font-body border-b border-border pb-2 last:border-0">
                    <span className="font-mono text-xs">{e.type}</span>
                    <span className="text-muted-foreground">{fmtDate(e.received_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recons.length > 0 && (
            <div className="bg-panel border border-border p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-display tracking-widest text-sm">RECONCILIATION</h3>
                <Button variant="outline" size="sm" onClick={() => exportReconCsv(recons, booking.id)} className="font-body">
                  <FileDown className="w-4 h-4 mr-1" /> CSV
                </Button>
              </div>
              <div className="space-y-4">
                {recons.map((r) => {
                  const b = r.before_snapshot ?? {};
                  const a = r.after_snapshot ?? {};
                  const d = r.deltas ?? {};
                  const row = (label: string, before: any, after: any, fmt?: (v: any) => string) => {
                    const changed = String(before) !== String(after);
                    return (
                      <div className="grid grid-cols-3 gap-2 text-xs font-body py-1 border-b border-border last:border-0">
                        <div className="text-muted-foreground font-mono uppercase tracking-widest">{label}</div>
                        <div className={changed ? "text-muted-foreground line-through" : ""}>{fmt ? fmt(before) : String(before ?? "—")}</div>
                        <div className={changed ? "text-primary" : ""}>{fmt ? fmt(after) : String(after ?? "—")}</div>
                      </div>
                    );
                  };
                  return (
                    <div key={r.id} className="border border-border p-3">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <p className="font-mono text-xs uppercase tracking-widest text-primary">{r.event_type}</p>
                          <p className="font-mono text-[10px] text-muted-foreground break-all">{r.stripe_event_id}</p>
                        </div>
                        <span className="font-mono text-xs text-muted-foreground">{fmtDate(r.created_at)}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border pb-1 mb-1">
                        <div>Field</div><div>Before</div><div>After</div>
                      </div>
                      {row("Total", b.total, a.total, (v) => fmtMoney(Number(v ?? 0)))}
                      {row("Amount paid", b.amount_paid, a.amount_paid, (v) => fmtMoney(Number(v ?? 0)))}
                      {row("Payment status", b.payment_status, a.payment_status)}
                      {row("Status", b.status, a.status)}
                      {(d.refunded_amount || d.amount_paid_delta || d.refund_id || d.invoice_email) && (
                        <div className="mt-3 pt-2 border-t border-border text-[11px] font-body text-muted-foreground space-y-0.5">
                          {d.refunded_amount != null && <p>Refunded: <span className="text-destructive">{fmtMoney(Number(d.refunded_amount))}</span></p>}
                          {d.amount_paid_delta != null && <p>Amount paid delta: <span className={Number(d.amount_paid_delta) < 0 ? "text-destructive" : "text-emerald-500"}>{Number(d.amount_paid_delta) >= 0 ? "+" : ""}{fmtMoney(Number(d.amount_paid_delta))}</span></p>}
                          {d.refund_id && <p>Refund ID: <span className="font-mono">{d.refund_id}</span> {d.refund_status ? `· ${d.refund_status}` : ""}</p>}
                          {d.failed && <p className="text-destructive">Refund failed{d.failure_reason ? ` — ${d.failure_reason}` : ""}</p>}
                          {d.invoice_email && <p>Invoice email: {d.invoice_email.last_template} · {d.invoice_email.last_status}</p>}
                          {d.full_refund && <p className="text-destructive">Full refund — booking moved to refunded</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-panel border border-border p-6">
            <h3 className="font-display tracking-widest text-sm mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4" /> EMAILS ({emails.length})
            </h3>
            <div className="mb-4 pb-4 border-b border-border grid grid-cols-2 gap-2 text-xs font-body">
              <div className="text-muted-foreground font-mono uppercase tracking-widest">Invoice last sent</div>
              <div className="text-right">{latestInvoiceEmail ? fmtDate(latestInvoiceEmail.created_at) : "Never"}</div>
              <div className="text-muted-foreground font-mono uppercase tracking-widest">Delivery result</div>
              <div className="text-right">
                {latestInvoiceEmail ? (
                  <Badge variant={latestInvoiceEmail.status === "sent" ? "default" : latestInvoiceEmail.status === "failed" || latestInvoiceEmail.status === "dlq" ? "destructive" : "secondary"}>
                    {latestInvoiceEmail.status}
                  </Badge>
                ) : "—"}
              </div>
              <div className="text-muted-foreground font-mono uppercase tracking-widest">Resend message id</div>
              <div className="text-right font-mono text-[10px] break-all">{latestInvoiceEmail?.resend_id ?? latestInvoiceEmail?.message_id ?? "—"}</div>
              <div className="text-muted-foreground font-mono uppercase tracking-widest">Last stripe webhook</div>
              <div className="text-right font-mono text-[10px] break-all">
                {latestInvoiceWebhookEvent ? `${latestInvoiceWebhookEvent.type} · ${latestInvoiceWebhookEvent.status}` : "—"}
              </div>
            </div>
            {emails.length === 0 ? (
              <p className="font-body text-sm text-muted-foreground">No transactional emails logged.</p>
            ) : (
              <div className="space-y-2">
                {emails.map((e) => (
                  <div key={e.id} className="flex justify-between items-center text-sm font-body border-b border-border pb-2 last:border-0 gap-3">
                    <div className="min-w-0">
                      <p className="truncate">{e.subject ?? e.template_name}</p>
                      <p className="text-xs text-muted-foreground font-mono truncate">{e.recipient_email} · {fmtDate(e.created_at)}</p>
                    </div>
                    <Badge variant={e.status === "sent" ? "default" : e.status === "failed" ? "destructive" : "secondary"}>{e.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {syncLogs.length > 0 && (
            <div className="bg-panel border border-border p-6">
              <h3 className="font-display tracking-widest text-sm mb-4">CALENDAR SYNC</h3>
              <div className="space-y-2">
                {syncLogs.slice(0, 10).map((s) => (
                  <div key={s.id} className="flex justify-between text-xs font-mono border-b border-border pb-2 last:border-0">
                    <span>{s.action} · {s.status}</span>
                    <span className="text-muted-foreground">{fmtDate(s.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {events.length > 0 && (
            <div className="bg-panel border border-border p-6">
              <h3 className="font-display tracking-widest text-sm mb-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> STRIPE EVENTS ({events.length})
              </h3>
              <div className="space-y-2">
                {events.slice(0, 15).map((e) => (
                  <div key={e.id} className="flex justify-between text-xs font-mono border-b border-border pb-2 last:border-0">
                    <span>{e.type}</span>
                    <span className="text-muted-foreground">{e.status} · {fmtDate(e.received_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-panel border border-border p-6">
            <h3 className="font-display tracking-widest text-sm mb-4 flex items-center gap-2">
              <History className="w-4 h-4" /> ADMIN AUDIT LOG ({audit.length})
            </h3>
            {audit.length === 0 ? (
              <p className="font-body text-sm text-muted-foreground">No admin actions logged yet.</p>
            ) : (
              <div className="space-y-2">
                {audit.map((a) => (
                  <div key={a.id} className="flex justify-between items-start text-xs font-mono border-b border-border pb-2 last:border-0 gap-3">
                    <div className="min-w-0">
                      <p className="uppercase tracking-widest text-primary">{a.action}</p>
                      <p className="text-muted-foreground truncate">
                        {actors[a.actor_id] ?? (a.actor_id ? a.actor_id.slice(0, 8) : "system")}
                      </p>
                      {a.after && (
                        <p className="text-muted-foreground text-[10px] mt-1 break-all">
                          {Object.entries(a.after).slice(0, 4).map(([k, v]) =>
                            `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`
                          ).join(" · ")}
                        </p>
                      )}
                    </div>
                    <span className="text-muted-foreground whitespace-nowrap">{fmtDate(a.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Partial refund dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Partial refund</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {addons.length > 0 && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  Remove add-ons (stock auto-restored)
                </Label>
                <div className="mt-2 space-y-2 border border-border p-3">
                  {addons.map((a) => {
                    const checked = refundAddonIds.includes(a.id);
                    return (
                      <label key={a.id} className="flex items-center gap-3 text-sm font-body cursor-pointer">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            setRefundAddonIds((prev) =>
                              v ? [...prev, a.id] : prev.filter((id) => id !== a.id),
                            );
                          }}
                        />
                        <span className="flex-1">
                          {a.addons?.name ?? "Add-on"}
                          {a.option_label ? ` — ${a.option_label}` : ""}
                          <span className="text-muted-foreground"> ({a.quantity} × {fmtMoney(Number(a.unit_price))})</span>
                        </span>
                        <span className="font-mono text-xs">{fmtMoney(Number(a.unit_price) * a.quantity)}</span>
                      </label>
                    );
                  })}
                </div>
                {selectedAddonsTotal > 0 && (
                  <p className="text-xs text-muted-foreground mt-2 font-mono">
                    Selected add-ons total: {fmtMoney(selectedAddonsTotal)}
                  </p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="refund-amount">Refund amount (USD)</Label>
              <Input
                id="refund-amount"
                type="number" step="0.01" min="0"
                placeholder={selectedAddonsTotal > 0 ? `Default: ${selectedAddonsTotal.toFixed(2)}` : "0.00"}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave blank to refund the selected add-ons total.
                Max refundable: {fmtMoney(Number(booking.amount_paid))}.
              </p>
            </div>

            <div>
              <Label htmlFor="refund-reason-code">Reason category (required)</Label>
              <Select value={refundReasonCode} onValueChange={setRefundReasonCode}>
                <SelectTrigger id="refund-reason-code"><SelectValue placeholder="Pick a category…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="duplicate">Duplicate charge</SelectItem>
                  <SelectItem value="customer_request">Customer request</SelectItem>
                  <SelectItem value="goodwill">Goodwill / courtesy</SelectItem>
                  <SelectItem value="scheduling_conflict">Scheduling conflict</SelectItem>
                  <SelectItem value="studio_issue">Studio / equipment issue</SelectItem>
                  <SelectItem value="overcharged">Overcharged / pricing error</SelectItem>
                  <SelectItem value="fraud">Fraud / chargeback</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="refund-reason">Reason (required, shared with customer)</Label>
              <Textarea
                id="refund-reason" rows={2}
                value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Explain to the customer why this refund is being issued…"
              />
            </div>

            <div>
              <Label htmlFor="refund-notes">Internal notes (admin-only)</Label>
              <Textarea
                id="refund-notes" rows={2}
                value={refundInternalNotes} onChange={(e) => setRefundInternalNotes(e.target.value)}
                placeholder="Optional context for the team. Not shared with the customer."
              />
            </div>

            <label className="flex items-center gap-2 text-sm font-body cursor-pointer">
              <Checkbox checked={refundNotify} onCheckedChange={(v) => setRefundNotify(!!v)} />
              Email the customer about this refund
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)} disabled={refunding}>Cancel</Button>
            <Button onClick={submitPartialRefund} disabled={refunding}>
              {refunding && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Issue refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditBookingDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        booking={booking}
        addons={addons}
        onSaved={load}
      />
    </AdminLayout>
  );
};

export default AdminBookingDetail;